/**
 * Priority Payment Modal — Braydin (Moonwrigh) direct-pay version
 * Handles payment for sending priority messages ("Buzz Moon's Phone")
 *
 * Restored CC's direct pay-per-message flow (card + wallet-offset) on top of
 * moon's Braydin (olive/cream) theme:
 * - Rail A: Saved card (one-click) via SavedCardSelector — backend auto-confirms
 * - Rail B: New card via Stripe CardElement — client-confirmed
 * - Rail C: Apple Pay / Google Pay via PaymentRequestButtonElement
 * - USD wallet balance applied as an OPTIONAL offset (useWallet checkbox)
 * - Prices shown in USD ($); server prices the message via /priority/check-limit
 * - Pay-then-send two-step flow preserved (initiate -> send)
 *
 * The whole app is wrapped in <Elements stripe={stripePromise}> in App.tsx,
 * so useStripe()/useElements() resolve here without an extra provider.
 *
 * Exported name, props interface, and caller invocation are unchanged.
 */

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { CardElement, PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js'
import type { PaymentRequest } from '@stripe/stripe-js'
import { getPaymentMethods } from '@/api/paymentMethods'
import type { PaymentMethod } from '@/types/paymentMethod'
import SavedCardSelector from '../wallet/SavedCardSelector'
import ReVerifyCardBanner from '../wallet/ReVerifyCardBanner'
import { usePreviousCard } from '@/hooks/usePreviousCard'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

// Format a USD amount: whole dollars show without decimals, otherwise 2dp
function formatUSD(amount: number): string {
  return `$${amount % 1 === 0 ? amount : amount.toFixed(2)}`
}

interface PriorityPaymentModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (paymentIntentId: string) => void
  recipientId: string
  messageContent: string
  conversationId: string
}

export function PriorityPaymentModal({
  isOpen,
  onClose,
  onSuccess,
  recipientId,
  messageContent,
  conversationId
}: PriorityPaymentModalProps) {
  const stripe = useStripe()
  const elements = useElements()

  const [priceInfo, setPriceInfo] = useState<{
    amount: number
    remaining: number
    dailyLimit: number
    usedToday: number
  } | null>(null)
  const [isLoadingInfo, setIsLoadingInfo] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  // Wallet state (USD balance, applied as an optional offset against the price)
  const [walletBalance, setWalletBalance] = useState<number>(0)
  const [useWallet, setUseWallet] = useState(true)

  // Saved cards state
  const [savedCards, setSavedCards] = useState<PaymentMethod[]>([])
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [saveCard, setSaveCard] = useState(true)
  const [isLoadingCards, setIsLoadingCards] = useState(true)

  // Apple Pay / Google Pay state
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)
  const [activePaymentMethod, setActivePaymentMethod] = useState<'digital' | 'card'>('card')

  // Display-only "re-verify your card" hint (does not affect payment logic)
  const { card: previousCard } = usePreviousCard(isOpen)

  // Load price info, wallet balance, and saved cards when modal opens
  useEffect(() => {
    if (isOpen) {
      checkLimitAndGetPrice()
      fetchSavedCards()
      fetchWalletBalance()
    }
  }, [isOpen])

  // Dismiss keyboard on mobile
  useEffect(() => {
    if (isOpen) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    }
  }, [isOpen])

  // Initialize Apple Pay / Google Pay once we have the price
  useEffect(() => {
    if (!stripe || !priceInfo || !isOpen) return

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Priority Message',
        amount: Math.round(priceInfo.amount * 100) // cents
      },
      requestPayerName: true,
      requestPayerEmail: true
    })

    pr.canMakePayment().then((result) => {
      if (result && (result.applePay === true || result.googlePay === true)) {
        setPaymentRequest(pr)
        setActivePaymentMethod('digital')
      } else {
        setActivePaymentMethod('card')
      }
    })

    pr.on('paymentmethod', async (e) => {
      try {
        setIsProcessing(true)
        const token = localStorage.getItem('auth_token')

        // Digital-wallet rail charges the full price (no wallet-offset params)
        const response = await fetch(`${API_URL}/api/messages/priority/initiate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            recipient_id: recipientId
          })
        })

        if (!response.ok) {
          throw new Error('Failed to initiate payment')
        }

        const { client_secret, payment_intent_id } = await response.json()

        const { error: confirmError } = await stripe.confirmCardPayment(
          client_secret,
          { payment_method: e.paymentMethod.id },
          { handleActions: false }
        )

        if (confirmError) {
          e.complete('fail')
          throw new Error(confirmError.message)
        }

        e.complete('success')

        await sendPriorityMessage(payment_intent_id)
        onSuccess(payment_intent_id)
        handleClose()
      } catch (err: any) {
        e.complete('fail')
        console.error('Priority payment error:', err)
        setError(err.message || 'Payment failed')
      } finally {
        setIsProcessing(false)
      }
    })
  }, [stripe, priceInfo, isOpen, recipientId])

  async function fetchWalletBalance() {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const response = await fetch(`${API_URL}/api/wallet/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setWalletBalance(data.balance || 0)
      }
    } catch (err) {
      console.error('Failed to fetch wallet balance:', err)
      setWalletBalance(0)
    }
  }

  async function fetchSavedCards() {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const response = await getPaymentMethods(token)
      setSavedCards(response.payment_methods || [])

      // Auto-select the default card if one exists
      const defaultCard = response.payment_methods.find(pm => pm.is_default)
      if (defaultCard) {
        setSelectedCardId(defaultCard.id)
      }
    } catch (err) {
      console.error('Failed to fetch saved cards:', err)
      setSavedCards([])
      setSelectedCardId(null)
    } finally {
      setIsLoadingCards(false)
    }
  }

  async function checkLimitAndGetPrice() {
    try {
      setIsLoadingInfo(true)
      setError(null)

      const token = localStorage.getItem('auth_token')
      if (!token) {
        setError('Please sign in to send priority messages')
        return
      }

      const response = await fetch(`${API_URL}/api/messages/priority/check-limit`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to check priority limit')
      }

      if (!data.canSend) {
        setError(`You've reached your daily limit of ${data.dailyLimit} priority messages. Please try again tomorrow.`)
        return
      }

      setPriceInfo({
        amount: data.pricePerMessage,
        remaining: data.remaining,
        dailyLimit: data.dailyLimit,
        usedToday: data.usedToday
      })
    } catch (err: any) {
      console.error('Check limit error:', err)
      setError(err.message || 'Failed to load priority messaging information')
    } finally {
      setIsLoadingInfo(false)
    }
  }

  async function handleRequestMore() {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      await fetch(`${API_URL}/api/messages/priority/request-more`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      alert('Your request has been received. Moon will review and may grant you additional priority messages.')
      onClose()
    } catch (err) {
      console.error('Request more error:', err)
    }
  }

  async function sendPriorityMessage(paymentIntentId: string) {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await fetch(`${API_URL}/api/messages/priority/send`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        recipient_id: recipientId,
        content: messageContent,
        payment_intent_id: paymentIntentId,
        attachments: []
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      console.error('[PriorityPaymentModal] Failed to send priority message:', errorData)
      throw new Error(errorData.error || 'Failed to send priority message')
    }

    return await response.json()
  }

  async function handlePurchase() {
    try {
      setIsProcessing(true)
      setError(null)

      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('Authentication required')
      }

      const totalAmount = priceInfo?.amount || 0

      // Wallet-as-offset split (mirrors CC)
      const walletAmount = useWallet ? Math.min(walletBalance, totalAmount) : 0
      const fullWalletPayment = walletAmount >= totalAmount

      // Case 1: wallet covers the full price — no card needed, server confirms
      if (fullWalletPayment) {
        const response = await fetch(`${API_URL}/api/messages/priority/initiate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            recipient_id: recipientId,
            use_wallet: true,
            wallet_amount: walletAmount
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to process wallet payment')
        }

        const data = await response.json()

        try {
          await sendPriorityMessage(data.payment_intent_id || 'wallet_payment')
          onSuccess(data.payment_intent_id || 'wallet_payment')
          handleClose()
        } catch (sendError: any) {
          console.error('[Priority] Payment succeeded but send failed:', sendError)
          setError(`Payment successful but failed to send message: ${sendError.message}. Please contact support.`)
        }
        return
      }

      // Need a card (possibly with a partial wallet offset)
      if (!stripe) {
        setError('Payment system not loaded')
        return
      }

      // Rail A: saved card (one-click, backend auto-confirms)
      if (savedCards.length > 0 && selectedCardId && selectedCardId !== 'new') {
        const selectedCard = savedCards.find(card => card.id === selectedCardId)
        if (!selectedCard) {
          throw new Error('Selected payment method not found')
        }

        const response = await fetch(`${API_URL}/api/messages/priority/initiate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            recipient_id: recipientId,
            saved_payment_method_id: selectedCard.stripe_payment_method_id,
            use_wallet: useWallet && walletAmount > 0,
            wallet_amount: walletAmount
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to initiate payment')
        }

        const data = await response.json()

        try {
          await sendPriorityMessage(data.payment_intent_id)
          onSuccess(data.payment_intent_id)
          handleClose()
        } catch (sendError: any) {
          console.error('[Priority] Payment succeeded but send failed:', sendError)
          setError(`Payment successful but failed to send message: ${sendError.message}. Please contact support.`)
        }
        return
      }

      // Rail B: new card (client-confirmed)
      if (!elements) {
        setError('Payment form not loaded')
        return
      }

      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        setError('Please enter your card details')
        return
      }

      const response = await fetch(`${API_URL}/api/messages/priority/initiate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_id: recipientId,
          save_payment_method: saveCard,
          use_wallet: useWallet && walletAmount > 0,
          wallet_amount: walletAmount
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to initiate payment')
      }

      const { client_secret, payment_intent_id } = await response.json()

      const { error: confirmError } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardElement
        }
      })

      if (confirmError) {
        throw new Error(confirmError.message)
      }

      try {
        await sendPriorityMessage(payment_intent_id)
        onSuccess(payment_intent_id)
        handleClose()
      } catch (sendError: any) {
        console.error('[Priority] Payment succeeded but send failed:', sendError)
        setError(`Payment successful but failed to send message: ${sendError.message}. Please contact support.`)
      }
    } catch (err: any) {
      console.error('Priority payment error:', err)
      setError(err.message || 'Payment failed')
    } finally {
      setIsProcessing(false)
    }
  }

  function handleClose() {
    setPriceInfo(null)
    setError(null)
    setSavedCards([])
    setSelectedCardId(null)
    setIsLoadingCards(true)
    setIsLoadingInfo(true)
    setPaymentRequest(null)
    setActivePaymentMethod('card')
    onClose()
  }

  if (!isOpen) return null

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        fontSize: '16px',
        color: '#2B2B26', // braydin-ink
        fontFamily: 'Inter, system-ui, sans-serif',
        '::placeholder': {
          color: '#B3AE9F' // braydin-taupe
        }
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444'
      }
    }
  }

  // Does the wallet cover the whole price? (drives which sections show)
  const walletCoversFull = useWallet && walletBalance >= (priceInfo?.amount || 0)

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-braydin-sand via-braydin-cream to-braydin-sand px-6 py-4 border-b border-braydin-sand">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-braydin-ink">Buzz Moon's Phone</h2>
              <p className="text-sm text-braydin-ink/70 mt-1">Your message will be pinned to the top of Moon's inbox and his phone will notify him of your message!</p>
            </div>
            <button
              onClick={handleClose}
              className="text-braydin-ink/50 hover:text-braydin-ink transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto flex-1">
          {isLoadingInfo || isLoadingCards ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-braydin-olive"></div>
              <p className="mt-4 text-braydin-ink/70">Loading...</p>
            </div>
          ) : error ? (
            <div className="text-center py-6">
              <div className="text-red-600 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-braydin-ink font-semibold mb-2">{error}</p>
              {error.includes('daily limit') && (
                <button
                  onClick={handleRequestMore}
                  className="mt-4 px-6 py-2 bg-braydin-olive text-white rounded-lg hover:bg-braydin-oliveDark transition-colors"
                >
                  Request More Priority Messages
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Price Info */}
              {priceInfo && (
                <div className="mb-6">
                  <div className="bg-braydin-cream border border-braydin-sand rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-braydin-ink font-medium">One Priority Message:</span>
                      <span className="text-2xl font-bold text-braydin-olive">{formatUSD(priceInfo.amount)}</span>
                    </div>
                    <div className="text-sm text-braydin-ink/70 mt-2">
                      Your message skips the line and pings Moon's phone right away. Limited slots each day.
                    </div>
                  </div>

                  {/* Wallet Balance Section (optional offset) */}
                  {walletBalance > 0 && (
                    <div className="mt-4 p-4 bg-braydin-sand2 border border-braydin-taupe rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">💰</span>
                          <div>
                            <p className="font-medium text-braydin-ink">Wallet Balance</p>
                            <p className="text-sm text-braydin-ink/70">{formatUSD(walletBalance)} available</p>
                          </div>
                        </div>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={useWallet}
                            onChange={(e) => setUseWallet(e.target.checked)}
                            className="h-5 w-5 text-braydin-olive focus:ring-braydin-olive border-braydin-taupe rounded mr-2"
                          />
                          <span className="text-sm font-medium text-braydin-ink">Use Wallet</span>
                        </label>
                      </div>
                      {useWallet && (
                        <div className="mt-2 pt-2 border-t border-braydin-taupe/60 text-sm">
                          {walletBalance >= priceInfo.amount ? (
                            <p className="text-braydin-sage">✓ Full payment from wallet — no card needed!</p>
                          ) : (
                            <p className="text-braydin-ink/80">
                              {formatUSD(Math.min(walletBalance, priceInfo.amount))} from wallet +
                              {' '}{formatUSD(priceInfo.amount - Math.min(walletBalance, priceInfo.amount))} from card
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-4 space-y-2 text-sm text-braydin-ink/70">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-braydin-sage mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Jumps to the very top of Moon's inbox</span>
                    </div>
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-braydin-sage mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>His phone buzzes because of you</span>
                    </div>
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-braydin-sage mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Best for messages that really matter</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Section */}
              <div className="space-y-4">
                {/* Payment Method Tabs (Apple/Google Pay vs Card) */}
                {paymentRequest && !walletCoversFull && (
                  <div className="flex gap-2 mb-4 bg-braydin-cream p-1 rounded-xl">
                    <button
                      type="button"
                      onClick={() => setActivePaymentMethod('digital')}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                        activePaymentMethod === 'digital'
                          ? 'bg-braydin-olive text-white shadow-md'
                          : 'text-braydin-ink/70 hover:text-braydin-ink'
                      }`}
                    >
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        Apple / Google Pay
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivePaymentMethod('card')}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                        activePaymentMethod === 'card'
                          ? 'bg-braydin-olive text-white shadow-md'
                          : 'text-braydin-ink/70 hover:text-braydin-ink'
                      }`}
                    >
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                          <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                        </svg>
                        Card
                      </span>
                    </button>
                  </div>
                )}

                {/* Apple Pay / Google Pay Section */}
                {activePaymentMethod === 'digital' && paymentRequest && !walletCoversFull ? (
                  <div className="space-y-4">
                    <div className="border border-braydin-sand rounded-xl p-4 bg-braydin-cream">
                      <PaymentRequestButtonElement
                        options={{
                          paymentRequest,
                          style: {
                            paymentRequestButton: {
                              type: 'buy',
                              theme: 'dark',
                              height: '48px'
                            }
                          }
                        }}
                      />
                    </div>
                    <p className="text-xs text-center text-braydin-ink/60">
                      Fast, secure checkout with Apple Pay or Google Pay
                    </p>
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={isProcessing}
                      className="w-full px-6 py-3 border border-braydin-taupe rounded-lg text-braydin-ink font-semibold hover:bg-braydin-cream transition-colors disabled:opacity-50"
                    >
                      Not Now
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Card section only shows when the wallet doesn't cover the full price */}
                    {!walletCoversFull && (
                      <>
                        {/* Saved Cards Selector */}
                        {savedCards.length > 0 && (
                          <SavedCardSelector
                            paymentMethods={savedCards}
                            selectedId={selectedCardId}
                            onSelect={setSelectedCardId}
                            onAddNew={() => setSelectedCardId('new')}
                          />
                        )}

                        {/* New Card Form */}
                        {(!savedCards.length || selectedCardId === 'new') && (
                          <>
                            <ReVerifyCardBanner card={previousCard} />
                            <div>
                              <label className="block text-sm font-medium text-braydin-ink mb-2">
                                {previousCard?.last4 ? 'Re-verify your card' : 'Card Details'}
                              </label>
                              <div className="border border-braydin-taupe rounded-lg p-3 bg-white">
                                <CardElement options={CARD_ELEMENT_OPTIONS} />
                              </div>
                            </div>

                            {/* Save Card Checkbox */}
                            <div className="flex items-center">
                              <input
                                id="save-card"
                                type="checkbox"
                                checked={saveCard}
                                onChange={(e) => setSaveCard(e.target.checked)}
                                className="h-4 w-4 text-braydin-olive focus:ring-braydin-olive border-braydin-taupe rounded"
                              />
                              <label htmlFor="save-card" className="ml-2 block text-sm text-braydin-ink">
                                Save card for future purchases
                              </label>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {/* Buttons for card / wallet payment */}
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="flex-1 px-6 py-3 border border-braydin-taupe rounded-lg text-braydin-ink font-semibold hover:bg-braydin-cream transition-colors"
                        disabled={isProcessing}
                      >
                        Not Now
                      </button>
                      <button
                        type="button"
                        onClick={handlePurchase}
                        disabled={!stripe || isProcessing || !priceInfo}
                        className="flex-1 px-6 py-3 bg-braydin-olive text-white rounded-lg font-semibold hover:bg-braydin-oliveDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isProcessing ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          walletCoversFull ? `Pay ${formatUSD(priceInfo?.amount || 0)} with Wallet` : 'Send'
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )

  // Render using portal to avoid form nesting issues
  return createPortal(modalContent, document.body)
}
